import { useState } from 'react';
import { Link, router } from '@inertiajs/react';
import TeacherShell from '../../../components/layout/TeacherShell';
import { Card, Button, Input, Textarea } from '../../../components/ui';
import { cn } from '../../../lib/cn';

const STATUS_STYLES = {
  draft: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  published: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
};

export default function AtlasMapSetsShow({ map_set }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(map_set.name || '');
  const [description, setDescription] = useState(map_set.description || '');
  const [status, setStatus] = useState(map_set.status || 'draft');
  const [saving, setSaving] = useState(false);

  const scrollTitle = (
    <>
      <Link href="/teacher/atlas_map_sets" className="hover:text-blue-600 dark:hover:text-blue-400">
        Map Sets
      </Link>
      {'  ›  '}
      {map_set.name}
    </>
  );

  const cancelEdit = () => {
    setName(map_set.name || '');
    setDescription(map_set.description || '');
    setStatus(map_set.status || 'draft');
    setEditing(false);
  };

  const saveEdit = (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    router.patch(
      `/teacher/atlas_map_sets/${map_set.id}`,
      { atlas_map_set: { name: name.trim(), description: description.trim() || null, status } },
      {
        onSuccess: () => setEditing(false),
        onFinish: () => setSaving(false),
      }
    );
  };

  const handleDelete = () => {
    if (!confirm(`Delete "${map_set.name}"?`)) return;
    router.delete(`/teacher/atlas_map_sets/${map_set.id}`);
  };

  const canEdit = map_set.is_own;

  return (
    <TeacherShell scrollTitle={scrollTitle}>
      <div className="mb-6">
        <Link
          href="/teacher/atlas_map_sets"
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white mb-4 transition-colors"
        >
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Map Sets
        </Link>

        {editing ? (
          <Card className="p-6">
            <form onSubmit={saveEdit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
                <Input value={name} onChange={(e) => setName(e.target.value)} required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  placeholder="Optional"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900/10 dark:focus:ring-white/20"
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                </select>
              </div>
              <div className="flex items-center gap-3">
                <Button type="submit" disabled={saving || !name.trim()}>
                  {saving ? 'Saving…' : 'Save'}
                </Button>
                <Button type="button" variant="secondary" onClick={cancelEdit}>
                  Cancel
                </Button>
              </div>
            </form>
          </Card>
        ) : (
          <div className="flex items-start justify-between gap-6">
            <div className="min-w-0">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{map_set.name}</h1>
                <span className={cn('inline-flex items-center px-2 py-0.5 rounded text-xs font-medium', STATUS_STYLES[map_set.status] || STATUS_STYLES.draft)}>
                  {map_set.status}
                </span>
              </div>
              {map_set.description && (
                <p className="mt-3 text-gray-600 dark:text-gray-300 whitespace-pre-wrap">
                  {map_set.description}
                </p>
              )}
              {!map_set.is_own && map_set.owner_name && (
                <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
                  Curated by {map_set.owner_name}
                </p>
              )}
            </div>

            {canEdit && (
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button variant="secondary" onClick={() => setEditing(true)}>
                  Edit
                </Button>
                <button
                  type="button"
                  onClick={handleDelete}
                  className="text-sm font-medium text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300 transition-colors"
                >
                  Delete
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <Card className="p-6">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Snapshots and the choropleth editor aren't built yet — this map set is name/description only for now.
        </p>
      </Card>
    </TeacherShell>
  );
}
